<template>
  <div class="chatbot-container">
    <section
      v-if="isOpen"
      class="chatbot-window"
      :class="{ 'chatbot-window--desktop': isDesktop, 'chatbot-window--resizable': canResize, 'chatbot-window--resizing': isResizing }"
      :style="windowStyle"
      :aria-label="$t('chatbot.title')"
    >
      <button
        v-if="canResize"
        type="button"
        class="chatbot-resize-handle"
        :aria-label="resizeLabel"
        :title="$t('chatbot.resizeHint')"
        @pointerdown="startResize"
        @pointermove="moveResize"
        @pointerup="finishResize"
        @pointercancel="finishResize"
        @lostpointercapture="finishResize"
        @keydown="resizeWithKeyboard"
      ></button>
      <div class="chatbot-header">
        <div>
          <div class="chatbot-header__title"><span>🤖</span> {{ $t('chatbot.title') }}</div>
          <div class="chatbot-header__quota">{{ $t('chatbot.retention') }}</div>
        </div>
        <button class="chatbot-header__close" :aria-label="$t('chatbot.close')" @click="toggleChat">&times;</button>
      </div>

      <div class="chatbot-toolbar">
        <button :disabled="isBusy" :aria-expanded="showHistory" @click="toggleHistory">
          {{ $t(showHistory ? 'chatbot.backToChat' : 'chatbot.history') }}
        </button>
        <span class="chatbot-toolbar__title" :title="displayTitle">{{ displayTitle }}</span>
        <button :disabled="isBusy" @click="startNewConversation">＋ {{ $t('chatbot.newConversation') }}</button>
      </div>

      <div v-if="showHistory" class="chatbot-history">
        <p class="chatbot-history__note">{{ $t('chatbot.historyNote') }}</p>
        <div v-if="historyError" class="chatbot-notice" role="alert">
          <span>{{ errorMessage(historyError) }}</span>
          <button :disabled="isLoadingHistory" @click="loadHistoryPage(historyRetryPage)">{{ $t('chatbot.reload') }}</button>
        </div>
        <p v-if="!isLoadingHistory && !historyError && !conversations.length" class="chatbot-status">{{ $t('chatbot.emptyHistory') }}</p>
        <button
          v-for="conversation in conversations"
          :key="conversation.id"
          class="chatbot-history__item"
          :class="{ 'chatbot-history__item--active': conversation.id === conversationId }"
          :disabled="isBusy"
          @click="selectConversation(conversation.id)"
        >
          <span>{{ conversation.title || $t('chatbot.aiConversation') }}</span>
          <time :datetime="conversation.updated_at">{{ formatDate(conversation.updated_at) }}</time>
        </button>
        <p v-if="isLoadingHistory" class="chatbot-status" role="status">{{ $t('chatbot.loadingHistory') }}</p>
        <button v-else-if="nextHistoryPage" class="chatbot-more" @click="loadHistoryPage(nextHistoryPage)">{{ $t('chatbot.moreConversations') }}</button>
      </div>

      <div v-else ref="messagesContainer" class="chatbot-messages" :aria-busy="isBusy">
        <div v-if="initializationError" class="chatbot-notice" role="alert">
          <span>{{ errorMessage(initializationError) }}</span>
          <button @click="loadLatestConversation">{{ $t('chatbot.reload') }}</button>
        </div>
        <p v-if="isInitializing || isLoadingMessages" class="chatbot-status" role="status">{{ $t('chatbot.loadingConversation') }}</p>
        <div v-if="conversationError" class="chatbot-notice" role="alert">
          <span>{{ errorMessage(conversationError) }}</span>
          <button @click="selectConversation(conversationId)">{{ $t('chatbot.reload') }}</button>
        </div>
        <button
          v-if="nextBeforeId"
          class="chatbot-more"
          :disabled="isLoadingOlder || isBusy"
          @click="loadOlderMessages"
        >{{ $t(isLoadingOlder ? 'chatbot.loading' : 'chatbot.olderMessages') }}</button>
        <div v-if="olderError" class="chatbot-notice" role="alert">{{ errorMessage(olderError) }}</div>
        <div
          v-for="msg in messages"
          :key="msg.id"
          class="chatbot-msg"
          :class="{
            'chatbot-msg--bot': msg.role === 'assistant',
            'chatbot-msg--user': msg.role === 'user',
            'chatbot-msg--error': msg.role === 'error'
          }"
        >
          <div class="chatbot-msg__content"><template v-for="(part, partIndex) in messageParts(messageContent(msg))" :key="partIndex"><router-link v-if="part.type === 'place'" class="chatbot-link" :to="part.href">{{ $t('chatbot.viewPlace') }}</router-link><a v-else-if="part.type === 'url'" class="chatbot-link" :href="part.href" target="_blank" rel="noopener noreferrer">{{ part.text }}</a><template v-else>{{ part.text }}</template></template></div>
          <time v-if="msg.created_at" class="chatbot-msg__time" :datetime="msg.created_at">{{ formatDate(msg.created_at) }}</time>
        </div>
        <div v-if="isSending" class="chatbot-typing" role="status" :aria-label="$t('chatbot.replying')"><span></span><span></span><span></span></div>
        <button v-if="retryText && !isBusy" class="chatbot-more" @click="editFailedMessage">{{ $t('chatbot.editRetry') }}</button>
      </div>

      <form v-if="!showHistory" class="chatbot-input" @submit.prevent="handleSend">
        <input
          ref="inputField"
          v-model="userInput"
          class="chatbot-input__field"
          :aria-label="$t('chatbot.ask')"
          :placeholder="$t('chatbot.placeholder')"
          maxlength="1000"
          :disabled="!canSend"
          @keydown.enter="preventCompositionSubmit"
        />
        <button class="chatbot-input__send" type="submit" :aria-label="$t('chatbot.send')" :disabled="!canSend || !userInput.trim()">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
          </svg>
        </button>
      </form>
    </section>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { getConversation, listConversations, sendMessage } from './chatService'
import { DEFAULT_CHAT_SIZE, getChatGeometry } from './chatGeometry'

const greeting = () => ({
  id: 'greeting',
  role: 'assistant',
  content: '',
  uiKey: 'chatbot.greeting'
})

export default {
  name: 'AiChatbot',
  data () {
    return {
      isOpen: false,
      showHistory: false,
      userInput: '',
      messages: [],
      conversations: [],
      conversationId: null,
      conversationTitle: '',
      nextHistoryPage: null,
      nextBeforeId: null,
      isInitializing: false,
      isLoadingHistory: false,
      isLoadingMessages: false,
      isLoadingOlder: false,
      isSending: false,
      initializationError: '',
      historyError: '',
      conversationError: '',
      olderError: '',
      historyRetryPage: 1,
      retryText: '',
      accountVersion: 0,
      conversationVersion: 0,
      historyVersion: 0,
      localMessageId: 0,
      isDisposed: false,
      isDesktop: window.innerWidth > 768,
      hasFinePointer: false,
      preferredSize: { ...DEFAULT_CHAT_SIZE },
      windowGeometry: null,
      isResizing: false
    }
  },
  computed: {
    ...mapGetters({ me: 'auth/me' }),
    userId () {
      return this.me && this.me.id ? String(this.me.id) : null
    },
    isBusy () {
      return this.isInitializing || this.isLoadingMessages || this.isSending
    },
    canSend () {
      return !!this.userId && !this.isBusy && !this.isLoadingOlder && !this.initializationError && !this.conversationError
    },
    canResize () {
      return this.isDesktop && this.hasFinePointer
    },
    windowStyle () {
      if (!this.isDesktop || !this.windowGeometry) return {}
      const { left, top, width, height } = this.windowGeometry
      return {
        left: `${left}px`,
        top: `${top}px`,
        right: 'auto',
        bottom: 'auto',
        width: `${width}px`,
        height: `${height}px`,
        maxWidth: 'none',
        maxHeight: 'none'
      }
    },
    displayTitle () {
      return this.conversationTitle || this.$t(this.conversationId ? 'chatbot.aiConversation' : 'chatbot.newConversation')
    },
    resizeLabel () {
      const size = this.windowGeometry || this.preferredSize
      return this.$t('chatbot.resizeLabel', { width: Math.round(size.width), height: Math.round(size.height) })
    }
  },
  watch: {
    userId: {
      immediate: true,
      handler () {
        this.resetForUser()
        if (this.userId) this.loadLatestConversation()
      }
    },
    '$route.fullPath' () {
      this.finishResize()
      this.$nextTick(this.refreshLayout)
    }
  },
  mounted () {
    this.emitter.on('toggleChatbot', this.toggleChat)
    this._layoutObserver = new MutationObserver(this.queueLayoutRefresh)
    if (window.ResizeObserver) this._sizeObserver = new ResizeObserver(this.queueLayoutRefresh)
    this._pointerMedia = window.matchMedia('(any-pointer: fine)')
    this.hasFinePointer = this._pointerMedia.matches
    if (this._pointerMedia.addEventListener) this._pointerMedia.addEventListener('change', this.updatePointerType)
    else this._pointerMedia.addListener(this.updatePointerType)
    window.addEventListener('resize', this.queueLayoutRefresh)
    window.addEventListener('scroll', this.queueLayoutRefresh, true)
    window.addEventListener('blur', this.finishResize)
    this.refreshLayout()
  },
  beforeUnmount () {
    this.isDisposed = true
    this.accountVersion += 1
    this.emitter.off('toggleChatbot', this.toggleChat)
    this.finishResize()
    window.cancelAnimationFrame(this._layoutFrame)
    this._layoutObserver.disconnect()
    if (this._sizeObserver) this._sizeObserver.disconnect()
    if (this._pointerMedia.removeEventListener) this._pointerMedia.removeEventListener('change', this.updatePointerType)
    else this._pointerMedia.removeListener(this.updatePointerType)
    window.removeEventListener('resize', this.queueLayoutRefresh)
    window.removeEventListener('scroll', this.queueLayoutRefresh, true)
    window.removeEventListener('blur', this.finishResize)
  },
  methods: {
    updatePointerType () {
      this.hasFinePointer = this._pointerMedia.matches
      if (!this.hasFinePointer) this.finishResize()
    },
    queueLayoutRefresh () {
      if (this.isDisposed || this._layoutFrame) return
      this._layoutFrame = window.requestAnimationFrame(() => {
        this._layoutFrame = null
        this.refreshLayout()
      })
    },
    refreshLayout () {
      if (this.isDisposed || !this.$el.parentElement) return
      const root = this.$el.parentElement
      this.isDesktop = window.innerWidth > 768
      if (!this.isDesktop) this.finishResize()

      // Observe only our route/layout containers, never Google's map subtree.
      // Direct child changes also catch lazy route components appearing later.
      const layoutNodes = new Set([
        root,
        ...Array.from(root.children).filter(node => !node.classList.contains('chatbot-container')),
        ...root.querySelectorAll('.places, .place, .histories, .history, .panel, .panel__right, .panel__left, .panel__content, .panel__map')
      ])
      if (!this._layoutNodes || this._layoutNodes.size !== layoutNodes.size || [...layoutNodes].some(node => !this._layoutNodes.has(node))) {
        this._layoutObserver.disconnect()
        layoutNodes.forEach(node => this._layoutObserver.observe(node, { childList: true, attributes: true, attributeFilter: ['class', 'style', 'hidden'] }))
        this._layoutNodes = layoutNodes
      }
      const sizeNodes = new Set(layoutNodes)

      const viewport = { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight }
      let bounds = viewport
      let largestArea = 0
      root.querySelectorAll('.panel__right .map > .map__iframe, .panel__map .map > .map__iframe').forEach(map => {
        sizeNodes.add(map)
        const rect = map.getBoundingClientRect()
        if (!rect.width || !rect.height || window.getComputedStyle(map).visibility === 'hidden') return
        const left = Math.max(0, rect.left)
        const top = Math.max(0, rect.top)
        const width = Math.max(0, Math.min(viewport.width, rect.right) - left)
        const height = Math.max(0, Math.min(viewport.height, rect.bottom) - top)
        if (width * height > largestArea) {
          bounds = { left, top, width, height }
          largestArea = width * height
        }
      })
      if (this._sizeObserver) {
        if (this._sizeNodes) {
          this._sizeNodes.forEach(node => {
            if (!sizeNodes.has(node)) this._sizeObserver.unobserve(node)
          })
        }
        sizeNodes.forEach(node => {
          if (!this._sizeNodes || !this._sizeNodes.has(node)) this._sizeObserver.observe(node)
        })
        this._sizeNodes = sizeNodes
      }
      if (this._mapBounds && ['left', 'top', 'width', 'height'].some(key => this._mapBounds[key] !== bounds[key])) this.finishResize()
      this._mapBounds = bounds
      this.windowGeometry = getChatGeometry(bounds, this.preferredSize)
    },
    setPreferredSize (size) {
      const geometry = getChatGeometry(this._mapBounds, size)
      this.preferredSize = { width: geometry.width, height: geometry.height }
      this.windowGeometry = geometry
    },
    startResize (event) {
      if (!this.canResize || event.pointerType === 'touch' || event.button !== 0 || event.isPrimary === false) return
      this.refreshLayout()
      this.finishResize()
      const target = event.currentTarget
      target.setPointerCapture(event.pointerId)
      this._resizeSession = {
        target,
        pointerId: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        width: this.windowGeometry.width,
        height: this.windowGeometry.height
      }
      this.isResizing = true
      // Native pointer focus keeps :focus-visible reserved for keyboard use.
      event.stopPropagation()
    },
    moveResize (event) {
      const drag = this._resizeSession
      if (!drag || event.pointerId !== drag.pointerId) return
      this.setPreferredSize({ width: drag.width + drag.x - event.clientX, height: drag.height + drag.y - event.clientY })
      event.preventDefault()
    },
    finishResize (event) {
      const drag = this._resizeSession
      if (drag && event && event.pointerId !== undefined && event.pointerId !== drag.pointerId) return
      this._resizeSession = null
      this.isResizing = false
      if (drag && drag.target.hasPointerCapture(drag.pointerId)) drag.target.releasePointerCapture(drag.pointerId)
    },
    resizeWithKeyboard (event) {
      if (!this.canResize || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home'].includes(event.key)) return
      event.preventDefault()
      event.stopPropagation()
      this.refreshLayout()
      this.finishResize()
      const step = event.shiftKey ? 50 : 20
      const size = { width: this.windowGeometry.width, height: this.windowGeometry.height }
      if (event.key === 'Home') Object.assign(size, DEFAULT_CHAT_SIZE)
      else if (event.key === 'ArrowLeft') size.width += step
      else if (event.key === 'ArrowRight') size.width -= step
      else if (event.key === 'ArrowUp') size.height += step
      else if (event.key === 'ArrowDown') size.height -= step
      this.setPreferredSize(size)
    },
    resetForUser () {
      this.finishResize()
      this.accountVersion += 1
      this.conversationVersion += 1
      this.historyVersion += 1
      this.messages = [greeting()]
      this.conversations = []
      this.conversationId = null
      this.conversationTitle = ''
      this.nextHistoryPage = null
      this.nextBeforeId = null
      this.userInput = ''
      this.retryText = ''
      this.showHistory = false
      this.isInitializing = false
      this.isLoadingHistory = false
      this.isLoadingMessages = false
      this.isLoadingOlder = false
      this.isSending = false
      this.initializationError = ''
      this.historyError = ''
      this.conversationError = ''
      this.olderError = ''
    },
    requestContext () {
      return { accountVersion: this.accountVersion, userId: this.userId }
    },
    isCurrentRequest (context) {
      return !this.isDisposed && context.accountVersion === this.accountVersion && context.userId === this.userId
    },
    toggleChat () {
      this.isOpen = !this.isOpen
      if (this.isOpen) {
        this.refreshLayout()
        this.scrollToBottom()
        this.focusInput()
      } else this.finishResize()
    },
    async loadLatestConversation () {
      if (!this.userId || this.isBusy) return
      const context = this.requestContext()
      this.isInitializing = true
      this.initializationError = ''
      const result = await listConversations()
      if (!this.isCurrentRequest(context)) return
      this.isInitializing = false
      if (!result.success) {
        this.initializationError = result
        return
      }
      this.conversations = result.conversations
      this.nextHistoryPage = result.nextPage
      if (this.conversations.length) await this.selectConversation(this.conversations[0].id)
      else this.startNewConversation()
    },
    toggleHistory () {
      if (this.isBusy) return
      this.showHistory = !this.showHistory
      if (this.showHistory) this.loadHistoryPage(1)
      else {
        this.scrollToBottom()
        this.focusInput()
      }
    },
    async loadHistoryPage (page) {
      if (!this.userId || this.isLoadingHistory) return
      const context = this.requestContext()
      const version = ++this.historyVersion
      this.historyRetryPage = page
      this.isLoadingHistory = true
      this.historyError = ''
      const result = await listConversations(page)
      if (!this.isCurrentRequest(context) || version !== this.historyVersion) return
      this.isLoadingHistory = false
      if (!result.success) {
        this.historyError = result
        return
      }
      const conversations = page === 1 ? [] : this.conversations
      this.conversations = this.uniqueMessages([...conversations, ...result.conversations])
      this.nextHistoryPage = result.nextPage
    },
    startNewConversation () {
      if (this.isBusy) return
      this.conversationVersion += 1
      this.conversationId = null
      this.conversationTitle = ''
      this.messages = [greeting()]
      this.nextBeforeId = null
      this.userInput = ''
      this.retryText = ''
      this.initializationError = ''
      this.conversationError = ''
      this.olderError = ''
      this.isLoadingOlder = false
      this.showHistory = false
      this.scrollToBottom()
      this.focusInput()
    },
    async selectConversation (id) {
      if (!id || this.isBusy) return
      const context = this.requestContext()
      const version = ++this.conversationVersion
      const conversation = this.conversations.find(item => item.id === id)
      this.conversationId = id
      this.conversationTitle = conversation ? conversation.title : ''
      this.messages = []
      this.userInput = ''
      this.retryText = ''
      this.nextBeforeId = null
      this.showHistory = false
      this.initializationError = ''
      this.conversationError = ''
      this.olderError = ''
      this.isLoadingOlder = false
      this.isLoadingMessages = true
      const result = await getConversation(id)
      if (!this.isCurrentRequest(context) || version !== this.conversationVersion) return
      this.isLoadingMessages = false
      if (!result.success) {
        this.conversationError = result
        return
      }
      this.messages = result.messages
      this.conversationTitle = result.conversation.title
      this.nextBeforeId = result.nextBeforeId
      this.scrollToBottom()
      this.focusInput()
    },
    async loadOlderMessages () {
      if (!this.nextBeforeId || this.isBusy || this.isLoadingOlder) return
      const context = this.requestContext()
      const version = this.conversationVersion
      this.isLoadingOlder = true
      this.olderError = ''
      const result = await getConversation(this.conversationId, this.nextBeforeId)
      if (!this.isCurrentRequest(context) || version !== this.conversationVersion) return
      const element = this.$refs.messagesContainer
      const height = element ? element.scrollHeight : 0
      const top = element ? element.scrollTop : 0
      this.isLoadingOlder = false
      if (!result.success) {
        this.olderError = result
        return
      }
      this.messages = this.uniqueMessages([...result.messages, ...this.messages])
      this.nextBeforeId = result.nextBeforeId
      this.$nextTick(() => {
        if (this.isCurrentRequest(context) && version === this.conversationVersion && element && element === this.$refs.messagesContainer) {
          element.scrollTop = top + element.scrollHeight - height
        }
      })
    },
    async handleSend () {
      const text = this.userInput.trim()
      if (!text || !this.canSend) return
      const context = this.requestContext()
      const optimisticId = `local-${++this.localMessageId}`
      this.messages.push({ id: optimisticId, role: 'user', content: text })
      this.userInput = ''
      this.retryText = ''
      this.isSending = true
      this.scrollToBottom()
      const result = await sendMessage(text, this.conversationId)
      if (!this.isCurrentRequest(context)) return
      this.isSending = false
      if (result.conversationId) this.conversationId = result.conversationId
      if (result.messages.length) {
        this.messages = this.uniqueMessages([
          ...this.messages.filter(message => message.id !== optimisticId && message.id !== 'greeting'),
          ...result.messages
        ])
      } else if (result.success) {
        this.messages.push({ id: `local-${++this.localMessageId}`, role: 'assistant', content: result.message })
      }
      if (!result.success) {
        if (!result.messages.some(message => message.role === 'error')) {
          this.messages.push({ id: `local-${++this.localMessageId}`, role: 'error', content: result.message, uiKey: result.errorKey })
        }
        if (result.status === 404 && this.conversationId) this.conversationError = result
        this.retryText = text
      }
      if (result.conversationId) {
        if (!this.conversationTitle) this.conversationTitle = text.slice(0, 80)
        const previous = this.conversations.find(item => item.id === this.conversationId)
        const updated = {
          ...previous,
          id: this.conversationId,
          title: this.conversationTitle,
          updated_at: result.messages.length ? result.messages[result.messages.length - 1].created_at : new Date().toISOString()
        }
        this.conversations = [updated, ...this.conversations.filter(item => item.id !== updated.id)]
      }
      this.scrollToBottom()
      this.focusInput()
    },
    editFailedMessage () {
      this.userInput = this.retryText
      this.retryText = ''
      this.focusInput()
    },
    preventCompositionSubmit (event) {
      if (event.isComposing || event.keyCode === 229) event.preventDefault()
    },
    uniqueMessages (items) {
      return [...new Map(items.map(item => [item.id, item])).values()]
    },
    errorMessage (error) {
      return error.errorKey ? this.$t(error.errorKey, error.errorParams || {}) : error.message || ''
    },
    messageContent (message) {
      return message.uiKey ? this.$t(message.uiKey) : message.content
    },
    messageParts (text) {
      const parts = []
      const links = /https?:\/\/[^\s<>"']+|\/places\/\d+\b/g
      let start = 0
      let match
      while ((match = links.exec(text)) !== null) {
        if (match.index > start) parts.push({ type: 'text', text: text.slice(start, match.index) })
        const href = match[0]
        parts.push({ type: href.startsWith('/places/') ? 'place' : 'url', text: href, href })
        start = match.index + href.length
      }
      if (start < text.length) parts.push({ type: 'text', text: text.slice(start) })
      return parts
    },
    formatDate (value) {
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleString(this.$i18n.locale === 'en' ? 'en-HK' : 'zh-HK', {
        timeZone: 'Asia/Hong_Kong',
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
    },
    focusInput () {
      this.$nextTick(() => {
        if (this.isOpen && this.canSend && this.$refs.inputField) this.$refs.inputField.focus()
      })
    },
    scrollToBottom () {
      this.$nextTick(() => {
        const element = this.$refs.messagesContainer
        if (element) element.scrollTop = element.scrollHeight
      })
    }
  }
}
</script>

<style lang="scss">
@import "style";
</style>
